import { motion } from 'framer-motion';

export const StartupsVisuals = () => {
    return (
        <div className="w-full h-full bg-white relative overflow-hidden flex flex-col items-center justify-center p-8 font-jakarta">
            {/* Background Grid */}
            <div className="absolute inset-0 grid grid-cols-6 grid-rows-6 opacity-[0.03] pointer-events-none">
                {[...Array(36)].map((_, i) => (
                    <div key={i} className="border-[0.5px] border-black" />
                ))}
            </div>

            {/* Floating Cards Container */}
            <div className="relative w-full max-w-[520px] aspect-[4/3]">
                {/* Main Card: Pitch Deck Slide */}
                <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.6 }}
                    className="absolute top-0 left-4 right-4 bottom-12 bg-white rounded-xl shadow-2xl border border-gray-200 p-8 flex flex-col z-20"
                >
                    {/* Header */}
                    <div className="flex justify-between items-start mb-8">
                        <div>
                            <h3 className="text-xl font-bold text-gray-900 leading-tight">Revenue Growth</h3>
                            <p className="text-xs text-gray-500 mt-1 font-medium">Year-over-Year Projections</p>
                        </div>
                        <div className="bg-blue-50 px-3 py-1.5 rounded-full border border-blue-100 flex items-center gap-2">
                            <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse" />
                            <span className="text-[10px] font-bold text-blue-700 uppercase tracking-wide">Series A Target</span>
                        </div>
                    </div>

                    {/* Content - Chart Area */}
                    <div className="flex-1 flex items-end gap-6 px-2 pb-2">
                        {/* Y-Axis Labels */}
                        <div className="flex flex-col justify-between h-32 text-[10px] text-gray-400 font-medium pb-1">
                            <span>$10M</span>
                            <span>$5M</span>
                            <span>$1M</span>
                            <span>0</span>
                        </div>

                        {/* Bars */}
                        <div className="flex-1 flex items-end justify-between gap-4 h-32">
                            {[
                                { year: '2023', val: '20%', color: 'bg-gray-200' },
                                { year: '2024', val: '45%', color: 'bg-blue-300' },
                                { year: '2025', val: '75%', color: 'bg-blue-600' }
                            ].map((item, i) => (
                                <div key={i} className="flex flex-col items-center gap-2 w-full group">
                                    <motion.div
                                        initial={{ height: 0 }}
                                        animate={{ height: item.val }}
                                        transition={{ delay: 0.4 + (i * 0.1), duration: 0.8 }}
                                        className={`w-full rounded-t-sm ${item.color} relative hover:opacity-90 transition-opacity`}
                                    >
                                        <div className="absolute -top-6 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity text-[10px] font-bold text-gray-600 bg-white shadow-sm px-1.5 py-0.5 rounded border border-gray-100">
                                            {item.val}
                                        </div>
                                    </motion.div>
                                    <span className="text-[10px] font-bold text-gray-500">{item.year}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Footer / Key Metrics */}
                    <div className="mt-6 pt-6 border-t border-gray-50 grid grid-cols-3 gap-4">
                        <div className="text-center">
                            <div className="text-[10px] text-gray-400 font-bold uppercase">ARR</div>
                            <div className="text-sm font-bold text-gray-900">$2.4M</div>
                        </div>
                        <div className="text-center border-l border-gray-50">
                            <div className="text-[10px] text-gray-400 font-bold uppercase">Growth</div>
                            <div className="text-sm font-bold text-green-600">+142%</div>
                        </div>
                        <div className="text-center border-l border-gray-50">
                            <div className="text-[10px] text-gray-400 font-bold uppercase">CAC</div>
                            <div className="text-sm font-bold text-gray-900">$450</div>
                        </div>
                    </div>
                </motion.div>

                {/* Background Card 1: Problem Slide */}
                <motion.div
                    initial={{ rotate: -5, x: -20, opacity: 0 }}
                    animate={{ rotate: -3, x: 0, opacity: 1 }}
                    transition={{ delay: 0.2, duration: 0.6 }}
                    className="absolute top-4 left-0 right-8 bottom-8 bg-gray-50 rounded-xl shadow-lg z-10 border border-gray-200 p-6 opacity-60"
                >
                    <div className="h-4 bg-gray-200 rounded w-1/3 mb-4" />
                    <div className="space-y-2">
                        <div className="h-2 bg-gray-100 rounded w-full" />
                        <div className="h-2 bg-gray-100 rounded w-5/6" />
                    </div>
                </motion.div>

                {/* Floating Element: Investment Badge */}
                <motion.div
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.8, type: "spring" }}
                    className="absolute -right-4 bottom-24 bg-white border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.12)] rounded-lg p-3 z-30 flex items-center gap-3"
                >
                    <div className="w-8 h-8 rounded-full bg-green-50 border border-green-100 flex items-center justify-center">
                        <span className="text-green-600 font-bold text-xs">$</span>
                    </div>
                    <div>
                        <div className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Pre-Money Valuation</div>
                        <div className="text-sm font-bold text-gray-900">$12.5M</div>
                    </div>
                </motion.div>

                {/* Floating Element: Investor Ready */}
                <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 1 }}
                    className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-[#1e293b] text-white px-5 py-2.5 rounded-full text-sm font-bold shadow-xl shadow-blue-900/20 flex items-center gap-2 group cursor-pointer hover:bg-black transition-colors"
                >
                    <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse group-hover:bg-green-300" />
                    Investor Ready
                </motion.div>
            </div>
        </div>
    );
};
